import {
	useGetOrderQuoteActivitiesQuery,
	useOnOrderQuoteActivityAddedSubscription,
	useSubscriptionRestored,
} from '@codezo/graphql';
import { ActivityTimeline, quoteActivityStyle, type ActivityItem } from '@codezo/quote-detail';
import { useEffect, useState } from 'react';
import { InitialsAvatar } from '../../../components/ui/initials-avatar';
import { useCurrencyStore } from '../../../stores/currency';

/**
 * The quote's activity, for the v2 page.
 *
 * Everything drawn lives in `@codezo/quote-detail`. What stays here is what a
 * package cannot have: the query, the live subscription, and the two app-owned
 * pieces the timeline is handed — the display currency and how this app draws an
 * actor.
 */
export function QuoteActivityV2({ quoteId }: { quoteId: string }) {
	const { selectedCurrency } = useCurrencyStore();

	const { data, loading, refetch } = useGetOrderQuoteActivitiesQuery({
		// Money changes in the header currency, like the rest of the page.
		variables: { orderQuoteId: quoteId, inCurrency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});

	// Events pushed over the socket since the last fetch. Cleared whenever the
	// query resolves, because by then they are in `data` too.
	const [live, setLive] = useState<ActivityItem[]>([]);
	useEffect(() => {
		if (data?.getOrderQuoteActivities) setLive([]);
	}, [data]);

	// Re-opened after a backend restart: fetch once for what happened in the gap.
	useSubscriptionRestored(() => void refetch(), ['OnOrderQuoteActivityAdded']);

	useOnOrderQuoteActivityAddedSubscription({
		// Same currency as the query, so a pushed row reads like a reloaded one.
		variables: { orderQuoteId: quoteId, inCurrency: selectedCurrency },
		onData: ({ data: pushed }) => {
			const added = pushed.data?.orderQuoteActivityAdded;
			if (!added) return;
			setLive((prev) => (prev.some((a) => a.id === added.id) ? prev : [added, ...prev]));
		},
	});

	return (
		<ActivityTimeline
			activities={[...live, ...(data?.getOrderQuoteActivities ?? [])]}
			loading={loading}
			resolveStyle={quoteActivityStyle}
			currency={selectedCurrency}
			renderAvatar={(name) => <InitialsAvatar name={name} size={15} />}
			/* The section already carries an "Activity" heading. */
			showHeader={false}
			/* An ordinary page section rather than a pinned rail — so it grows and
			   the page scrolls, instead of nesting a scroll box inside the page. */
			scrollable={false}
		/>
	);
}
